<div class="brief-details-page h-full w-full overflow-y-auto bg-transparent p-4">
  @switch (loadingState()) {
    @case ('loading') {
      <atom-loading-inline title="Loading brief details" message="Fetching brief information…" />
    }
    @case ('error') {
      <p class="m-0 text-sm text-destructive">Unable to load brief details.</p>
    }
    @case ('loaded') {
      @if (brief(); as b) {
        <div class="flex flex-col gap-4 max-w-6xl w-full mx-auto">
          <section
            class="flex flex-wrap items-center justify-between gap-4 rounded-xl border-2 border-zinc-400/35 bg-zinc-200/80 p-4 sm:p-5 dark:border-zinc-500/45 dark:bg-indigo-800/20">

            <div class="flex gap-4">
              <div class="flex items-center gap-4 min-w-0">
                @if (b.businessAccount?.companyLogo) {
                  <img
                    [src]="imageService.getImageSource(b.businessAccount!.companyLogo, 'gallery')"
                    alt="Logo for {{ b.businessAccount?.companyName }}"
                    class="size-16 shrink-0 rounded-lg object-cover border border-border/60" />
                } @else {
                  <div
                    class="size-16 shrink-0 rounded-lg border border-border/60 bg-muted/50 flex items-center justify-center text-secondary"
                    aria-hidden="true">
                    <svg class="size-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <path d="M14 2v6h6" />
                    </svg>
                  </div>
                }
              </div>
              <div>
                <p class="m-0 text-xs text-secondary">Brief type</p>
                <p class="m-0 mt-1 text-sm text-primary">{{ briefTypeLabel(b) }}</p>
                @if (briefTypeDetail(b); as detail) {
                  <p class="m-0 mt-1 text-xs text-secondary">{{ detail }}</p>
                }
              </div>
            </div>


            <div class="flex flex-col items-end gap-1 min-w-0 ml-auto">
              <h1 class="text-lg font-semibold text-primary m-0 text-right">{{ b.title }}</h1>
              <div class="flex min-w-0 items-center justify-end gap-2">
                <p
                  class="m-0 max-w-72 overflow-hidden text-ellipsis whitespace-nowrap font-mono text-xs text-secondary md:max-w-96"
                  [title]="b.id">
                  {{ b.id }}
                </p>
                <button
                  type="button"
                  class="inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border bg-zinc-200/60 text-secondary transition-[opacity,color,border-color] duration-150 hover:text-primary hover:opacity-[0.85] dark:bg-zinc-600/60"
                  [class]="copyIdButtonClass()"
                  (click)="copyBriefId(b.id)"
                  [attr.aria-label]="copyIdAriaLabel()">
                  @if (briefIdCopied()) {
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      aria-hidden="true">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  } @else {
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      aria-hidden="true">
                      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                    </svg>
                  }
                </button>
              </div>
            </div>
          </section>

          <!-- <section
            class="rounded-xl border-2 border-zinc-400/35 bg-zinc-200/80 p-4 sm:p-5 dark:border-zinc-500/45 dark:bg-indigo-800/20">
            <h2 class="m-0 mb-3 text-sm font-semibold tracking-wide text-primary">Description</h2>
            <p class="m-0 mt-1 text-sm text-primary whitespace-pre-wrap">
              {{ b.description || '—' }}
            </p>
          </section> -->

          <section
            class="rounded-xl border-2 border-zinc-400/35 bg-zinc-200/80 p-4 sm:p-5 dark:border-zinc-500/45 dark:bg-indigo-800/20">
            <h2 class="m-0 mb-3 text-sm font-semibold tracking-wide text-primary">Key message</h2>
            @if (hasKeyMessage(b)) {
              <div class="m-0 text-sm text-primary prose prose-sm max-w-none dark:prose-invert" [innerHTML]="keyMessageHtml(b)"></div>
            } @else {
              <p class="m-0 text-sm text-primary">—</p>
            }
          </section>

          <section
            class="rounded-xl border-2 border-zinc-400/35 bg-zinc-200/80 p-4 sm:p-5 dark:border-zinc-500/45 dark:bg-indigo-800/20">
            <h2 class="m-0 mb-3 text-sm font-semibold tracking-wide text-primary">Brief requirements</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <p class="m-0 text-xs text-secondary">Creators required</p>
                <p class="m-0 mt-1 text-sm text-primary">{{ b.requiredCreators }}</p>
              </div>
              <div>
                <p class="m-0 text-xs text-secondary">Videos per creator</p>
                <p class="m-0 mt-1 text-sm text-primary">{{ briefAsk(b).videoCount }}</p>
              </div>
              @if (briefAsk(b).source === 'pieces') {
              <div>
                <p class="m-0 text-xs text-secondary">Posts</p>
                <p class="m-0 mt-1 text-sm text-primary">{{ briefAsk(b).postCount }}</p>
                <p class="m-0 mt-1 text-xs text-secondary">{{ briefAsk(b).detail }}</p>
              </div>
              }
              <div>
                <p class="m-0 text-xs text-secondary">Video duration</p>
                <div class="mt-1 flex items-center gap-2">
                  <p class="m-0 text-sm text-primary">{{ videoDurationLabel(b) }}</p>
                  @if (videoDurationIcon(b); as durationIcon) {
                    <img [src]="durationIcon" alt="" class="h-8 w-8" aria-hidden="true" />
                  }
                </div>
              </div>
              <div>
                <p class="m-0 text-xs text-secondary">Video aspect ratio</p>
                <div class="mt-1 flex items-center gap-2">
                  <p class="m-0 text-sm text-primary">{{ videoAspectRatioLabel(b) }}</p>
                  @if (videoAspectRatioIcon(b); as formatIcon) {
                    <img [src]="formatIcon" alt="" class="h-10 w-auto" aria-hidden="true" />
                  }
                </div>
              </div>
              <div>
                <p class="m-0 text-xs text-secondary">Noodle enabled</p>
                <p class="m-0 mt-1 text-sm text-primary">{{ noodleEnabledLabel(b) }}</p>
              </div>
            </div>
          </section>

          <section
            class="rounded-xl border-2 border-zinc-400/35 bg-zinc-200/80 p-4 sm:p-5 dark:border-zinc-500/45 dark:bg-indigo-800/20">
            <h2 class="m-0 mb-3 text-sm font-semibold tracking-wide text-primary">Campaign &amp; brand</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <p class="m-0 text-xs text-secondary">Campaign</p>
                <p class="m-0 mt-1 text-sm text-primary">{{ b.campaign.campaignName || '—' }}</p>
              </div>
              <div>
                <p class="m-0 text-xs text-secondary">Brand</p>
                <p class="m-0 mt-1 text-sm text-primary">{{ b.businessAccount?.companyName || '—' }}</p>
              </div>
              <div>
                <p class="m-0 text-xs text-secondary">Business account ID</p>
                <p class="m-0 mt-1 text-sm font-mono text-primary">{{ b.businessAccountId || '—' }}</p>
              </div>
            </div>
          </section>

          <section
            class="rounded-xl border-2 border-zinc-400/35 bg-zinc-200/80 p-4 sm:p-5 dark:border-zinc-500/45 dark:bg-indigo-800/20">
            <h2 class="m-0 mb-3 text-sm font-semibold tracking-wide text-primary">Status &amp; settings</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <p class="m-0 text-xs text-secondary">Visibility</p>
                <p class="m-0 mt-1 text-sm text-primary">{{ visibilityLabel(b) }}</p>
              </div>
              <div>
                <p class="m-0 text-xs text-secondary">Created</p>
                <p class="m-0 mt-1 text-sm text-primary">{{ formatDate(b.createdDate) }}</p>
              </div>
              <div>
                <p class="m-0 text-xs text-secondary">Auto pilot</p>
                <p class="m-0 mt-1 text-sm text-primary">{{ b.enableAutoPilot ? 'Enabled' : 'Disabled' }}</p>
              </div>
              <div>
                <p class="m-0 text-xs text-secondary">Product</p>
                <p class="m-0 mt-1 text-sm text-primary">{{ b.product.productName || '—' }}</p>
              </div>
            </div>
          </section>
        </div>
      }
    }
  }
</div>
